import type { SignedStep } from "../api/types.js";

/**
 * DCTA-131 (ADR-0014, ruling PQ-02) — a chain on the confirmation card, read from the signed steps alone.
 *
 * When one step of a bundle depends on another (`depends_on`), the order is information the customer needs:
 * the money moves first, then it is spent ("Invest five hundred dollars from my savings into OCBC and DBS by
 * half": the bank adds ONE own-account move, savings → trading, and each buy is half of it). The card then
 * numbers its operations in the order the bank runs them — the payload's `index`, never `source_index`,
 * which a funding move SHARES with its buy — and links a step to the row above it when it follows from that
 * row or from the same step that row follows from (two buys spending one move). A bundle whose steps stand
 * alone (E6) is no chain: no numbers, no links, the card as it was. Nothing here is arithmetic.
 *
 * `passThrough`: an own-account move whose destination a later step of the same bundle spends from (the
 * funding move). The signed per-account totals then hold the same money twice — S$500.00 out of savings and
 * up to S$500.00 out of the trading account for one S$500.00 the customer said — so the card prints no sum
 * of them (no header total, no figure on the button) and leaves each account's signed total as it is.
 */
export interface ChainRow {
  /** The step's number on the card, in the order the bank runs it; only on a chain. */
  readonly n?: string;
  /** This row follows from the row above it: the rail joins them. */
  readonly linked: boolean;
}

export interface CardChain {
  /** Some step depends on another on this card: the rows are numbered. */
  readonly numbered: boolean;
  readonly rows: readonly ChainRow[];
  /** A move between the customer's own accounts that a later step spends from (see above). */
  readonly passThrough: boolean;
}

/** The account a step takes its money from, when it takes any. */
function payingAccount(step: SignedStep): string | undefined {
  if (step.type === "securities_order") return step.side === "buy" ? step.settlement_account.account_id : undefined;
  if (step.type === "funds_transfer" || step.type === "bill_payment") return step.debit_account.account_id;
  return undefined;
}

export function chainOf(steps: readonly SignedStep[]): CardChain {
  const ids = new Set(steps.map((s) => s.step_id));
  // Only a dependency on a step that is on this card counts: a refused or dropped step is not drawn.
  const deps = steps.map((s) => (s.depends_on ?? []).filter((id) => ids.has(id) && id !== s.step_id));
  const numbered = steps.length > 1 && deps.some((d) => d.length > 0);
  const rows = steps.map((s, i): ChainRow => {
    const above = i === 0 ? undefined : steps[i - 1];
    const follows = above === undefined ? [] : [above.step_id, ...(deps[i - 1] ?? [])];
    const linked = numbered && (deps[i] ?? []).some((id) => follows.includes(id));
    return numbered ? { n: s.index, linked } : { linked: false };
  });
  const passThrough = steps.some(
    (move) =>
      move.type === "funds_transfer" &&
      move.to_account !== undefined &&
      steps.some((later, j) => (deps[j] ?? []).includes(move.step_id) && payingAccount(later) === move.to_account?.account_id),
  );
  return { numbered, rows, passThrough };
}
